import { Box, Button, Flex, Slider, SliderFilledTrack, SliderThumb, SliderTrack, Text } from '@chakra-ui/react';
import { useCallback, useState } from 'react';
import Cropper from 'react-easy-crop';

interface ImageCropperProps {
    imageSrc: string;
    onCropComplete: (croppedImageFile: File) => void;
    onCancel: () => void;
}

export default function ImageCropper({ imageSrc, onCropComplete, onCancel }: ImageCropperProps) {
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);

    const onCropChange = (crop: { x: number; y: number }) => {
        setCrop(crop);
    };

    const onCropCompleteInternal = useCallback((croppedArea: any, croppedAreaPixels: any) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    const onZoomChange = (zoom: number) => {
        setZoom(zoom);
    };

    const createCroppedImage = async () => {
        try {
            const image = new Image();
            image.src = imageSrc;
            await new Promise((resolve) => {
                image.onload = resolve;
            });

            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            if (!ctx) return;

            // Set canvas size to the cropped size
            canvas.width = croppedAreaPixels.width;
            canvas.height = croppedAreaPixels.height;

            // Draw the cropped image onto the canvas
            ctx.drawImage(
                image,
                croppedAreaPixels.x,
                croppedAreaPixels.y,
                croppedAreaPixels.width,
                croppedAreaPixels.height,
                0,
                0,
                croppedAreaPixels.width,
                croppedAreaPixels.height
            );

            // Convert canvas to Blob
            canvas.toBlob((blob) => {
                if (!blob) return;
                const file = new File([blob], 'cropped-icon.png', { type: 'image/png' });
                onCropComplete(file);
            }, 'image/png');
        } catch (e) {
            console.error(e);
        }
    };

    return (
        <Flex flexDir="column" h="400px" w="100%">
            <Box position="relative" flex={1} w="100%" bg="gray.900" borderRadius="md" overflow="hidden">
                <Cropper
                    image={imageSrc}
                    crop={crop}
                    zoom={zoom}
                    aspect={1}
                    cropShape="round"
                    onCropChange={onCropChange}
                    onCropComplete={onCropCompleteInternal}
                    onZoomChange={onZoomChange}
                />
            </Box>
            <Flex alignItems="center" gap={4} mt={4}>
                <Text fontWeight="bold" whiteSpace="nowrap">Zoom:</Text>
                <Slider
                    value={zoom}
                    min={1}
                    max={3}
                    step={0.1}
                    onChange={(val) => setZoom(val)}
                >
                    <SliderTrack>
                        <SliderFilledTrack />
                    </SliderTrack>
                    <SliderThumb />
                </Slider>
            </Flex>
            <Flex gap={3} mt={4} justifyContent="flex-end">
                <Button variant="outline" onClick={onCancel}>Cancel</Button>
                <Button colorScheme="purple" onClick={createCroppedImage}>Apply Crop</Button>
            </Flex>
        </Flex>
    );
}
